/**
 * backgroundTasks — Sprint 4C
 * A tiny runner so slow, image-heavy jobs (backup prep, PDF render) run async and
 * non-blocking instead of freezing a modal/view. Progress is broadcast via window
 * CustomEvents — the same decoupled pattern AutoSaveIndicator already uses for
 * 'trip-autosave-status' — and mirrored to a native notification (notify.js).
 *
 * Events (detail = a snapshot of the task):
 *   wayfare-task-progress · wayfare-task-done · wayfare-task-error
 *
 * The done event's detail carries the in-memory `result` (Blob/object) directly —
 * same page, no serialization — so the UI can offer Share/Download with a gesture.
 */
import { ensureNotifyPermission, notifyProgress, notifyDone } from './notify';

const tasks = new Map(); // id -> task snapshot

const emit = (name, task) => {
  try { window.dispatchEvent(new CustomEvent(name, { detail: { ...task } })); } catch { /* SSR/no-DOM */ }
};

export function getTask(id) { return tasks.get(id); }
export function isRunning(id) { return tasks.get(id)?.status === 'running'; }

/**
 * Run a background task. Idempotent by id (a second call while running is ignored).
 * @param {{ id:string, kind:string, label:string, run:(onProgress:Function)=>Promise<any> }} def
 *   run(onProgress) — onProgress({ phase?, current?, total? }) reports progress.
 * @returns the task snapshot (already running).
 */
export function runTask({ id, kind, label, run }) {
  const existing = tasks.get(id);
  if (existing && existing.status === 'running') return existing;

  const task = { id, kind, label, status: 'running', phase: '', current: 0, total: 0, result: null, error: null };
  tasks.set(id, task);
  emit('wayfare-task-progress', task);

  // Ask for notification permission lazily (first task only); non-blocking.
  ensureNotifyPermission().catch(() => {});

  const onProgress = (p) => {
    if (p && typeof p === 'object') Object.assign(task, p);
    emit('wayfare-task-progress', task);
    const body = task.total > 0
      ? `${task.phase || ''} ${task.current}/${task.total}`.trim()
      : (task.phase || label);
    notifyProgress(`wf-task-${id}`, label, body);
  };

  Promise.resolve()
    .then(() => run(onProgress))
    .then((result) => {
      task.status = 'done';
      task.result = result;
      emit('wayfare-task-done', task);
      notifyDone(`wf-task-${id}`, label, (result && result.doneText) || 'Listo');
    })
    .catch((err) => {
      task.status = 'error';
      task.error = err?.message || String(err);
      emit('wayfare-task-error', task);
      notifyDone(`wf-task-${id}`, label, 'No se pudo completar');
    });

  return task;
}

/** Cooperative yield — let the browser paint between chunks of heavy work. */
export function yieldToUI() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => setTimeout(resolve, 0));
    else setTimeout(resolve, 0);
  });
}
